import { useEffect, useState } from 'react';
import { useKeyboardHeight } from '../hooks/useKeyboardHeight';

// Anchored portal dropdown positioning — positions a card just below `fieldRef`,
// portaled into `portalRef` (a fixed, inset:0, pointer-events:none container).
// Extracted from NewContact.tsx's local usePickerDropdown so form controls
// (InlineTypePicker, the Display Name picker) can share one implementation.

type PickerRect = { top: number; left?: number; right?: number; maxHeight: number; minWidth?: number; width?: number };

export function usePortalPickerRect(
  isOpen: boolean,
  fieldRef: React.RefObject<HTMLElement | null>,
  portalRef: React.RefObject<HTMLElement | null>,
  cardRef: React.RefObject<HTMLElement | null>,
  close: () => void,
  align: 'left' | 'right',
  matchWidth = false,
  // With matchWidth: a hard width equal to the trigger's (long unwrapped content wraps inside it) instead of a
  // minimum width the card may grow beyond.
  exactWidth = false,
) {
  const [rect, setRect] = useState<PickerRect | null>(null);
  // Re-runs the positioning effect when the keyboard opens/closes (e.g. tapping the dropdown's search).
  const keyboardHeight = useKeyboardHeight();

  // Clearing the rect when the picker closes is computed during render
  // (React's "adjust state when a dependency changes" pattern) rather than
  // as a setState call inside the effect below, which now only handles the
  // actual DOM-measurement/listener setup while open.
  const [prevIsOpen, setPrevIsOpen] = useState(isOpen);
  if (prevIsOpen !== isOpen) {
    setPrevIsOpen(isOpen);
    if (!isOpen && rect !== null) setRect(null);
  }

  useEffect(() => {
    if (!isOpen) return;

    const computeRect = (): PickerRect | null => {
      const field = fieldRef.current;
      const portal = portalRef.current;
      if (!field || !portal) return null;
      const box = field.getBoundingClientRect();
      const anchor = portal.getBoundingClientRect();
      const minWidth = matchWidth && !exactWidth ? box.width : undefined;
      const width = matchWidth && exactWidth ? box.width : undefined;

      // Flip upward when there isn't enough room below the anchor but there is
      // more above it — matches Contact.tsx's usePortalDropdownPosition. Only
      // trusts the card's own measured height; before that's known (first
      // paint) it opens downward, then the rAF recompute corrects it.
      const tabBar = globalThis.document.querySelector('ion-tab-bar');
      const tabBarHeight = tabBar ? tabBar.getBoundingClientRect().height : 0;
      // The native app runs the keyboard in resize:none mode (see useKeyboardHeight), so
      // window.innerHeight still includes the area under the keyboard — treat the keyboard's top
      // edge as the bottom of the usable screen, or a dropdown opens "below" behind it.
      const spaceBelow = window.innerHeight - keyboardHeight - tabBarHeight - box.bottom;
      const spaceAbove = box.top;
      // Natural (uncapped) content height, NOT getBoundingClientRect().height: that already
      // reflects the inline maxHeight set below, so measuring it shrank the card to fit under the
      // anchor and the flip test could never pass — the list ran off the bottom. scrollHeight is
      // independent of maxHeight, so the decision is stable (same intent as Contact.tsx's version).
      // Natural height with our own inline maxHeight cleared (restored right after, same frame, so no
      // visible change). scrollHeight under-reports for cards whose children shrink inside a capped
      // flex column (the phone country list), and getBoundingClientRect() alone reports the height
      // already shrunk to fit below the anchor — either way the card could never be seen to not fit.
      const card = cardRef.current;
      let cardHeight = 0;
      if (card) {
        const prevMaxHeight = card.style.maxHeight;
        card.style.maxHeight = '';
        cardHeight = card.getBoundingClientRect().height;
        card.style.maxHeight = prevMaxHeight;
      }
      const flipUp = cardHeight > 0 && spaceBelow < cardHeight + 4 && spaceAbove > spaceBelow;

      const available = (flipUp ? spaceAbove : spaceBelow) - 12;
      let maxHeight = Math.max(96, Math.min(320, available));
      let top = flipUp
        ? box.top - anchor.top - Math.min(cardHeight, maxHeight) - 4
        : box.bottom - anchor.top + 4;

      // Hard guarantee while the keyboard is up (it doesn't resize the screen in the native app): the
      // whole card — including a search box at its top — must end above the keyboard's top edge, even
      // when it fits neither cleanly below nor above its anchor. Slide it up over the anchor if
      // needed, and shrink it only if it still can't fit between the top of the screen and the keyboard.
      if (keyboardHeight > 0) {
        const limit = window.innerHeight - keyboardHeight - 8;
        const minAbsTop = 56; // stay clear of the status bar / header
        const height = Math.min(cardHeight || maxHeight, maxHeight);
        if (anchor.top + top + height > limit) {
          const newAbsTop = Math.max(minAbsTop, limit - height);
          top = newAbsTop - anchor.top;
          maxHeight = Math.max(96, Math.min(maxHeight, limit - newAbsTop));
        }
      }

      return align === 'left'
        ? { top, left: box.left - anchor.left, maxHeight, minWidth, width }
        : { top, right: anchor.right - box.right, maxHeight, minWidth, width };
    };

    const applyRect = (r: PickerRect) => {
      const card = cardRef.current;
      if (!card) return;
      card.style.top = `${r.top}px`;
      card.style.left = r.left !== undefined ? `${r.left}px` : '';
      card.style.right = r.right !== undefined ? `${r.right}px` : '';
      card.style.maxHeight = `${r.maxHeight}px`;
      card.style.minWidth = r.minWidth !== undefined ? `${r.minWidth}px` : '';
      card.style.width = r.width !== undefined ? `${r.width}px` : '';
    };

    const initial = computeRect();
    setRect(initial);

    let raf2 = 0;
    const raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => {
        const r = computeRect();
        if (r) {
          setRect(r);
          applyRect(r);
        }
      });
    });

    // Recompute once the card's real height is measurable / whenever it changes
    // — needed for the flip-up decision, which depends on cardHeight.
    const resizeObserver = new ResizeObserver(() => {
      const r = computeRect();
      if (r) {
        setRect(r);
        applyRect(r);
      }
    });
    let attachRaf = 0;
    const tryAttachObserver = () => {
      if (cardRef.current) {
        resizeObserver.observe(cardRef.current);
        return;
      }
      attachRaf = requestAnimationFrame(tryAttachObserver);
    };
    tryAttachObserver();

    const handleScroll = () => {
      const r = computeRect();
      if (r) applyRect(r);
    };
    const handleResize = () => {
      const r = computeRect();
      if (r) {
        setRect(r);
        applyRect(r);
      }
    };
    const handleOutsidePointer = (e: PointerEvent) => {
      const target = e.target as Node;
      if (fieldRef.current?.contains(target)) return;
      if (portalRef.current?.contains(target)) return;
      close();
    };

    document.addEventListener('scroll', handleScroll, true);
    window.addEventListener('resize', handleResize);
    document.addEventListener('pointerdown', handleOutsidePointer);

    return () => {
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
      cancelAnimationFrame(attachRaf);
      resizeObserver.disconnect();
      document.removeEventListener('scroll', handleScroll, true);
      window.removeEventListener('resize', handleResize);
      document.removeEventListener('pointerdown', handleOutsidePointer);
    };
  }, [isOpen, fieldRef, portalRef, cardRef, close, align, matchWidth, exactWidth, keyboardHeight]);

  return rect;
}
